/* assets/wb-anim.js
   WB Universal Animation System (KEYFRAMES)
   ----------------------------------------
   One JS file to power animations everywhere.

   Use in HTML:
   - data-anim="left|right|up|down|zoom-in|zoom-out|left-zoom|right-zoom|up-zoom|down-zoom|fade"
   - data-anim-delay="500"        (ms)
   - data-anim-duration="1000"    (ms)
   - data-anim-distance="128"     (px)
   - data-anim-ease="ease"        (optional)
   - data-anim-once               (boolean)

   Reveal modes:
   - data-anim-mode="scroll"      (default) -> reveal when element enters view
   - data-anim-mode="load"        -> reveal immediately on load (good for fixed footer)

   Scroll container support:
   - data-anim-root="#container"  -> observe inside an overflow-y-scroll container

   External trigger (visible-based):
   - data-anim-trigger=".selector" -> reveal when ANY matching trigger is intersecting

   Presence trigger (exists-in-DOM-based):
   - data-anim-present=".selector" -> reveal as soon as selector EXISTS anywhere in DOM

   Shopify theme editor supported (section reloads).
*/

(() => {
  const SELECTOR = '[data-anim]';
  const VISIBLE = 'is-visible';

  // ---------------------------
  // Utils
  // ---------------------------
  const parseIntSafe = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const rafReveal = (el) => {
    el.classList.remove(VISIBLE);
    void el.offsetWidth;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => el.classList.add(VISIBLE));
    });
  };

  function applyVars(el) {
    const delay = parseIntSafe(el.getAttribute('data-anim-delay'));
    const duration = parseIntSafe(el.getAttribute('data-anim-duration'));
    const distance = parseIntSafe(el.getAttribute('data-anim-distance'));
    const ease = el.getAttribute('data-anim-ease');

    if (delay != null) el.style.setProperty('--anim-delay', `${delay}ms`);
    if (duration != null) el.style.setProperty('--anim-duration', `${duration}ms`);
    if (distance != null) el.style.setProperty('--anim-distance', `${distance}px`);
    if (ease) el.style.setProperty('--anim-ease', ease);
  }

  // ---------------------------
  // IntersectionObservers
  // ---------------------------

  // 1) Element-in-view observers (one per root: viewport or custom scroll container)
  const elementIOByRoot = new Map();

  function ensureElementIO(rootEl) {
    const key = rootEl || '__viewport__';
    if (elementIOByRoot.has(key)) return elementIOByRoot.get(key);
    if (!('IntersectionObserver' in window)) return null;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const once = el.hasAttribute('data-anim-once');

          if (entry.isIntersecting) {
            el.classList.add(VISIBLE);
            if (once) io.unobserve(el);
          } else {
            if (!once) el.classList.remove(VISIBLE);
          }
        });
      },
      {
        root: rootEl || null, // null = viewport
        threshold: 0.01,
        rootMargin: '0px 0px 0px 0px',
      }
    );

    elementIOByRoot.set(key, io);
    return io;
  }

  // 2) External trigger observer (shared)
  let triggerIO = null;

  // key: trigger selector string
  // value: { targets:Set<Element>, visibleCount:number }
  const triggerGroups = new Map();

  function ensureTriggerIO() {
    if (triggerIO) return triggerIO;
    if (!('IntersectionObserver' in window)) return null;

    triggerIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const key = entry.target.__wbTriggerKey;
          const group = key ? triggerGroups.get(key) : null;
          if (!group) return;

          const wasVisible = entry.target.__wbVisible || false;
          const isVisible = entry.isIntersecting;

          if (isVisible && !wasVisible) group.visibleCount++;
          if (!isVisible && wasVisible) group.visibleCount--;

          entry.target.__wbVisible = isVisible;

          const show = group.visibleCount > 0;

          group.targets.forEach((t) => {
            const once = t.hasAttribute('data-anim-once');
            if (show) t.classList.add(VISIBLE);
            else if (!once) t.classList.remove(VISIBLE);
          });
        });
      },
      { threshold: 0.01, rootMargin: '0px' }
    );

    return triggerIO;
  }

  function registerTriggerTarget(targetEl, triggerSelector) {
    const sel = (triggerSelector || '').trim();
    if (!sel) return;

    if (!triggerGroups.has(sel)) {
      triggerGroups.set(sel, { targets: new Set(), visibleCount: 0 });
    }
    triggerGroups.get(sel).targets.add(targetEl);
  }

  function bindTriggerObservers() {
    const io = ensureTriggerIO();

    // No IO => reveal all trigger-based targets so nothing stays hidden
    if (!io) {
      triggerGroups.forEach((group) => group.targets.forEach((t) => rafReveal(t)));
      return;
    }

    triggerGroups.forEach((group, sel) => {
      const triggers = Array.from(document.querySelectorAll(sel));

      // No triggers found => reveal targets (avoid stuck hidden)
      if (!triggers.length) {
        group.targets.forEach((t) => rafReveal(t));
        return;
      }

      triggers.forEach((tr) => {
        if (tr.__wbTriggerBound) return;
        tr.__wbTriggerBound = true;
        tr.__wbTriggerKey = sel;
        tr.__wbVisible = false;
        io.observe(tr);
      });
    });
  }

  // ---------------------------
  // Presence trigger (exists-in-DOM)
  // ---------------------------
  // key: selector string
  // value: Set<Element> targets waiting for that selector to exist
  const presenceGroups = new Map();
  let presenceObserver = null;

  function registerPresenceTarget(targetEl, presentSelector) {
    const sel = (presentSelector || '').trim();
    if (!sel) return;

    // If it's already present in the DOM, reveal immediately
    if (document.querySelector(sel)) {
      rafReveal(targetEl);
      return;
    }

    if (!presenceGroups.has(sel)) presenceGroups.set(sel, new Set());
    presenceGroups.get(sel).add(targetEl);

    ensurePresenceObserver();
  }

  function ensurePresenceObserver() {
    if (presenceObserver) return presenceObserver;

    // Observe DOM mutations: when something is inserted, check if selectors now exist
    presenceObserver = new MutationObserver(() => {
      if (!presenceGroups.size) return;

      for (const [sel, targets] of presenceGroups.entries()) {
        if (!document.querySelector(sel)) continue;

        targets.forEach((t) => rafReveal(t));
        presenceGroups.delete(sel);
      }

      // If nothing left to watch, disconnect
      if (!presenceGroups.size) {
        presenceObserver.disconnect();
        presenceObserver = null;
      }
    });

    presenceObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    return presenceObserver;
  }

  // ---------------------------
  // Init
  // ---------------------------
  function init(root = document) {
    const els = [];

    // Inclure root s'il est lui-même [data-anim]
    if (root.nodeType === 1 && root.matches && root.matches(SELECTOR)) {
      els.push(root);
    }

    // Inclure tous les descendants [data-anim]
    els.push(...root.querySelectorAll(SELECTOR));

    if (!els.length) return;

    els.forEach((el) => {
      // Avoid binding twice (Shopify reloads)
      if (el.__wbAnimBound) return;
      el.__wbAnimBound = true;

      applyVars(el);

      // 0) Presence trigger
      const presentSel = el.getAttribute('data-anim-present');
      if (presentSel) {
        registerPresenceTarget(el, presentSel);
        return;
      }

      // 1) External trigger
      const triggerSel = el.getAttribute('data-anim-trigger');
      if (triggerSel) {
        registerTriggerTarget(el, triggerSel);
        return;
      }

      // 2) Mode-based
      const mode = (el.getAttribute('data-anim-mode') || 'scroll').toLowerCase();
      if (mode === 'load') {
        rafReveal(el);
        return;
      }

      // 3) Scroll container support
      const rootSelector = el.getAttribute('data-anim-root');
      const rootEl = rootSelector ? document.querySelector(rootSelector) : null;

      const io = ensureElementIO(rootEl);
      if (!io) {
        el.classList.add(VISIBLE);
        return;
      }

      io.observe(el);
    });

    bindTriggerObservers();
  }

  // ---------------------------
  // Boot + Shopify editor + late DOM injection
  // ---------------------------

  // Observe late-injected nodes (Shopify, apps, dynamic footer content)
  const wbAnimMO = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue; // element only
        init(node);
      }
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    wbAnimMO.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  });

  // Shopify theme editor support
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => e?.target && init(e.target));
    document.addEventListener('shopify:section:select', (e) => e?.target && init(e.target));
    document.addEventListener('shopify:section:reorder', () => init(document));
  }
})();

// =========================================================
// TYPEWRITER (2 modes)
// - SIMPLE mode (scroll/hero): .tw-source -> types into .tw-target
// - GHOST mode (chapters/click/open): .tw-ghost defines final layout,
//   .tw-target overlays and reveals characters without mid-word breaks,
//   keeps <br> visible, keeps punctuation attached.
// Cursor kept EMPTY.
// =========================================================
(() => {
  const sleep = (ms, signal) =>
    new Promise((resolve, reject) => {
      const t = setTimeout(resolve, ms);
      if (signal) {
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(t);
            reject(new DOMException('Aborted', 'AbortError'));
          },
          { once: true }
        );
      }
    });

  const isTextNode = (n) => n && n.nodeType === Node.TEXT_NODE;
  const isElementNode = (n) => n && n.nodeType === Node.ELEMENT_NODE;

  // =========================================================
  // SIMPLE MODE (tw-source -> append characters into text nodes)
  // =========================================================
  function buildTypingJobs(sourceRoot, targetRoot) {
    const jobs = [];

    function walk(srcNode, tgtParent) {
      if (isTextNode(srcNode)) {
        const text = srcNode.nodeValue || '';
        const empty = document.createTextNode('');
        tgtParent.appendChild(empty);
        jobs.push({ text, out: empty });
        return;
      }

      if (isElementNode(srcNode)) {
        const tag = srcNode.tagName.toLowerCase();
        if (tag === 'script' || tag === 'style') return;

        const clone = srcNode.cloneNode(false);
        tgtParent.appendChild(clone);

        for (const child of Array.from(srcNode.childNodes)) {
          walk(child, clone);
        }
      }
    }

    for (const child of Array.from(sourceRoot.childNodes)) {
      walk(child, targetRoot);
    }

    return jobs;
  }

  async function typeSimple(container, { speed = 25 } = {}) {
    const target = container.querySelector('.tw-target');
    const source = container.querySelector('.tw-source');
    if (!target || !source) return;

    if (container.dataset.typed === 'true') return;

    if (container._twAbort) container._twAbort.abort();
    const controller = new AbortController();
    container._twAbort = controller;
    const signal = controller.signal;

    target.innerHTML = '';

    const cursor = document.createElement('span');
    cursor.className = 'tw-cursor';
    cursor.textContent = ''; // empty cursor

    const jobs = buildTypingJobs(source, target);
    target.appendChild(cursor);

    try {
      for (const job of jobs) {
        const full = job.text;
        if (!full) continue;

        for (let i = 0; i < full.length; i++) {
          job.out.nodeValue += full[i];
          cursor.remove();
          target.appendChild(cursor);
          await sleep(speed, signal);
        }
      }

      cursor.remove();
      container.dataset.typed = 'true';
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('Typewriter(simple) error:', e);
    }
  }

  // =========================================================
  // GHOST MODE (tw-ghost -> overlay reveal with NO mid-word breaks)
  // =========================================================
  function wrapTokenToWordChars(token) {
    // token: word-like chunk incl punctuation (e.g. "THIS…", "coffee," etc.)
    const word = document.createElement('span');
    word.className = 'tw-word';

    for (let i = 0; i < token.length; i++) {
      const ch = token[i];
      const span = document.createElement('span');
      span.className = 'tw-char';
      span.textContent = ch === ' ' ? '\u00A0' : ch;
      word.appendChild(span);
    }
    return word;
  }

  function wrapTextNodeAsWordRuns(text) {
    // Clean indentation noise from pretty HTML
    const cleaned = (text || '').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n');

    // Drop whitespace-only nodes (prevents blank top spacing)
    if (cleaned.trim().length === 0) return null;

    // Split by whitespace, keeping it (so wrapping happens between words)
    const parts = cleaned.split(/(\s+)/);
    const frag = document.createDocumentFragment();

    for (const part of parts) {
      if (!part) continue;

      if (/^\s+$/.test(part)) {
        // Keep whitespace as text nodes (wrap opportunities)
        frag.appendChild(document.createTextNode(part));
      } else {
        // Word chunks: no mid-word wrap, punctuation stays attached
        frag.appendChild(wrapTokenToWordChars(part));
      }
    }

    return frag;
  }

  function buildTargetFromGhost(ghostEl, targetEl) {
    const clone = ghostEl.cloneNode(true);

    // Replace ONLY text nodes with word/char spans.
    // <br> stays as-is => you will SEE line breaks.
    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);

    for (const tn of textNodes) {
      const replacement = wrapTextNodeAsWordRuns(tn.nodeValue || '');
      if (!replacement) {
        tn.remove();
        continue;
      }
      tn.parentNode.replaceChild(replacement, tn);
    }

    targetEl.innerHTML = '';
    while (clone.firstChild) targetEl.appendChild(clone.firstChild);
  }

  async function typeGhost(container, { speed = 25 } = {}) {
    const ghost = container.querySelector('.tw-ghost');
    const target = container.querySelector('.tw-target');
    if (!ghost || !target) return;

    if (container.dataset.typed === 'true') return;

    if (container._twAbort) container._twAbort.abort();
    const controller = new AbortController();
    container._twAbort = controller;
    const signal = controller.signal;

    // Build final layout immediately (chars hidden, layout reserved)
    buildTargetFromGhost(ghost, target);

    const cursor = document.createElement('span');
    cursor.className = 'tw-cursor';
    cursor.textContent = ''; // empty cursor
    target.appendChild(cursor);

    const chars = target.querySelectorAll('.tw-char');

    try {
      for (let i = 0; i < chars.length; i++) {
        chars[i].classList.add('is-on');

        // keep cursor at end
        cursor.remove();
        target.appendChild(cursor);

        await sleep(speed, signal);
      }

      cursor.remove();
      container.dataset.typed = 'true';
    } catch (e) {
      if (e?.name !== 'AbortError') console.error('Typewriter(ghost) error:', e);
    }
  }

  // =========================================================
  // Dispatcher
  // data-tw-mode="simple" | "ghost"
  // default: simple
  // =========================================================
  function runOne(el) {
    const mode = (el.dataset.twMode || 'simple').toLowerCase();
    const speed = Number(el.dataset.speed || 25);
    const delay = Number(el.dataset.delay || 0);

    setTimeout(() => {
      if (mode === 'ghost') return typeGhost(el, { speed });
      return typeSimple(el, { speed });
    }, delay);
  }

  function runTypewritersIn(root) {
    root.querySelectorAll('[data-typewriter]').forEach(runOne);
  }

  // =========================================================
  // Scroll trigger (for HERO, Prelude, any section/container)
  // Add data-typewriter-on-scroll on the container that enters viewport.
  // =========================================================
  function resetTypewritersIn(root) {
    root.querySelectorAll('[data-typewriter]').forEach((el) => {
      el.dataset.typed = 'false';

      if (el._twAbort) el._twAbort.abort();

      const target = el.querySelector('.tw-target');
      if (target) target.innerHTML = '';
    });
  }

  function initTypewriterOnScroll() {
    const roots = document.querySelectorAll('[data-typewriter-on-scroll]');
    if (!roots.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const root = entry.target;

          if (!entry.isIntersecting) continue;
          if (entry.intersectionRatio < 0.6) continue;
          if (root.dataset.typewriterPlayed === 'true') continue;

          runTypewritersIn(root);
          root.dataset.typewriterPlayed = 'true';
          io.unobserve(root);
        }
      },
      {
        threshold: [0.25, 0.6, 0.9]
      }
    );

    roots.forEach((r) => io.observe(r));
  }

  // =========================================================
  // Chapters trigger (open/click)
  // Watches aria-hidden on .chapter-content[data-content]
  // and runs typewriters when aria-hidden becomes "false".
  // =========================================================
  function initChaptersOnOpen() {
    const panels = document.querySelectorAll('.chapter-content[data-content]');
    if (!panels.length) return;

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'aria-hidden') continue;

        const panel = m.target;
        if (panel.getAttribute('aria-hidden') === 'true') continue;

        requestAnimationFrame(() => runTypewritersIn(panel));
      }
    });

    panels.forEach((p) => {
      mo.observe(p, { attributes: true });

      // If already open on load
      if (p.getAttribute('aria-hidden') === 'false') {
        requestAnimationFrame(() => runTypewritersIn(p));
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initTypewriterOnScroll();
    initChaptersOnOpen();
  });
})();

